import { Box, Heading, Separator } from "@chakra-ui/react-v3";
import { buildDayjs, ISO_DATE } from "@metriport/shared/common/date";
import { SmartObservation } from "@metriport/fhir-sdk";
import { ResourceAnalysis } from "../components/resource-analysis";
import { ResourceTimelineLayout } from "../components/resource-timeline-layout";
import { SeriesChart } from "../components/series-chart";
import { SidePanelLayout } from "../components/side-panel-sections/side-panel-layout";
import { LabRowData } from "../tables/lab";
import { LabTimelineItem } from "./lab-timeline-item";
import { getDateForResourceType } from "../utils/resource-value-extractors";

export type LabSidePanelContentProps = {
  data: LabRowData;
};

export function LabSidePanelContent({ data }: LabSidePanelContentProps) {
  const series = data.originalData;
  const shouldShowChart = series.resources.length > 1;

  // Sort resources by date (newest first)
  const sortedResources = [...series.resources]
    .map(resource => ({
      resource: resource as SmartObservation,
      date: getDateForResourceType(resource),
    }))
    .filter((item): item is { resource: SmartObservation; date: string } =>
      Boolean(item.date && item.date !== "-")
    )
    .sort((a, b) => buildDayjs(b.date).diff(buildDayjs(a.date)));

  return (
    <SidePanelLayout
      title="Laboratory Result"
      subtitle={data.observation}
      metadata={`${series.resources.length} results • ${buildDayjs(series.earliestDate).format(
        ISO_DATE
      )} to ${buildDayjs(series.latestDate).format(ISO_DATE)}`}
    >
      <ResourceAnalysis />

      {shouldShowChart && (
        <>
          <Separator borderColor="border" />
          <Box>
            <Heading size="md" mb={4} color="fg">
              Trend Over Time
            </Heading>
            <Box p={4} borderWidth="1px" borderColor="border" borderRadius="md" bg="bg.subtle">
              <SeriesChart
                series={series}
                chartSeries={data.chartSeries}
                yAxisLabel="Value"
                height={300}
                width={600}
              />
            </Box>
          </Box>
        </>
      )}

      <Separator borderColor="border" />

      <ResourceTimelineLayout
        title={`Results (${sortedResources.length})`}
        items={sortedResources.map((item, index) => ({
          date: item.date,
          content: (
            <LabTimelineItem
              key={item.resource.id || index}
              resource={item.resource}
              isFirst={index === 0}
            />
          ),
        }))}
      />
    </SidePanelLayout>
  );
}
